import { FC, useEffect, useMemo, useState } from "react";
import useIsComponentMounted from "../../../common/hooks/use-is-component-mounted";
import { useAppContext } from "../../../context/app.context";
import useRouteFinishedListingUserPermissions from "./common/user-permissions/use-route-finished-listing-user-permissions";
import usePlanEntryFinishedList from "../../../common/services/plan-entry/finished-list/use-plan-entry-finished-list";
import useRouteFinishedListingFilterDao from "./filters/use-route-finished-listing-filter.dao";
import routeTranslationsHelper from "../../../languages/route-translations.helper";
import appTranslationsHelper from "../../../languages/app-translations.helper";
import RouteFinishedListingFilter from "./common/types/route-finished-listing-filter";
import RouteFinishedListingSortKey from "./common/types/route-finished-listing-sort-key";
import useAbort from "../../../common/hooks/use-abort";
import usePagination from "../../../common/hooks/use-pagination";
import HeadingComponent from "../../../common/components/heading/heading.component";
import RouteFinishedListingFilterComponent from "./filters/route-finished-listing-filter.component";
import CardComponent from "../../../common/components/card/card.component";
import RouteFinishedListingTableComponent from "./table/route-finished-listing-table.component";
import ListingPaginationComponent from "../../../common/components/listing/pagination/listing-pagination.component";
import routeFinishedListingItemFactory from "./common/factory/route-finished-listing-item.factory";
import RouteFinishedListingItem from "./common/types/route-finished-listing-item";
import useDocumentTitle from "../../../common/hooks/use-document-title";
import routeBreadcrumbsHelper from "../common/breadcrumbs/route-breadcrumbs.helper";
import PlanEntryFinishedListParams from "../../../common/services/plan-entry/finished-list/plan-entry-finished-list-params";
import RouteFinishedListingLoadParamsFactory from "./common/factory/route-finished-listing-load-params.factory";

type RouteFinishedListingProps = {};

const RouteFinishedListingComponent: FC<RouteFinishedListingProps> = (
  props,
) => {
  const translations = routeTranslationsHelper.getFinishedListingTranslations();

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  const isComponentMounted = useIsComponentMounted();

  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const userPermissions = useRouteFinishedListingUserPermissions();

  const routeFinishedList = usePlanEntryFinishedList();

  const filtersDao = useRouteFinishedListingFilterDao();

  const routeFinishedListAbort = useAbort();

  const [filters, setFilters] = useState<RouteFinishedListingFilter[]>(
    filtersDao.getFilters(),
  );

  const [sortKey, setSortKey] = useState<RouteFinishedListingSortKey>(
    filtersDao.getSortKey() ?? RouteFinishedListingSortKey.HUMAN_ID_DESC,
  );

  const [expandedRowsOrderUuids, setExpandedRowsOrderUuids] = useState<
    string[]
  >([]);

  const { page, pageSize, setPage, setPageSize } = usePagination({
    totalResults: routeFinishedList.data.totalCount,
    defaultPageSize: filtersDao.getPageSize(),
    defaultPage: filtersDao.getPage(),
  });

  useDocumentTitle(documentTitleTranslations.routeFinishedListing);

  useEffect(() => {
    const breadcrumbs = routeBreadcrumbsHelper.getFinishedListingBreadcrumbs();
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  useEffect(() => {
    setExpandedRowsOrderUuids([]);
    loadListingItems();

    return () => routeFinishedListAbort.revoke();
  }, [page, pageSize, filters, sortKey]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    window.scroll({ top: 0, behavior: "smooth" });
    setExpandedRowsOrderUuids([]);
  }, [page]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    setExpandedRowsOrderUuids([]);
  }, [sortKey]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    filtersDao.saveFilters(filters, sortKey, page, pageSize);
  }, [filters, sortKey, page, pageSize]);

  const listingItems: RouteFinishedListingItem[] = useMemo(() => {
    return routeFinishedListingItemFactory.createListingItems(
      routeFinishedList.data.data,
    );
  }, [routeFinishedList.data.data]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    setPage(1);
  }, [pageSize, filters]);

  const loadListingItems = async () => {
    const params: PlanEntryFinishedListParams =
      RouteFinishedListingLoadParamsFactory.create(
        page,
        pageSize,
        filters,
        sortKey,
      );

    routeFinishedList.load(params, routeFinishedListAbort.signal);
  };

  return (
    <div className="route_finished_listing">
      <HeadingComponent title={translations.header.headingText} />
      <RouteFinishedListingFilterComponent
        filters={filters}
        onFiltersChange={setFilters}
        onSortKeyChange={setSortKey}
        sortKey={sortKey}
        hasAccessToTaxiCorporation={userPermissions.hasAccessToTaxiCorporation}
      />
      <CardComponent classNames={{ root: "mt-4" }}>
        <RouteFinishedListingTableComponent
          listingItems={listingItems}
          isError={routeFinishedList.isError}
          isLoading={routeFinishedList.isLoading}
          expandedRowsUuids={expandedRowsOrderUuids}
          onExpandedRowsUuidsChange={setExpandedRowsOrderUuids}
        />
        <ListingPaginationComponent
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          page={page}
          pageSize={pageSize}
          totalResults={routeFinishedList.data.totalCount}
        />
      </CardComponent>
    </div>
  );
};

export default RouteFinishedListingComponent;
